import {
  isPwaFeatureOn,
  type PwaFlagSnapshot,
} from "@/features/pwa/lib/pwa-flags";
import { pwaDebugLog, pwaDebugWarn } from "./pwa-debug";

export interface ServiceWorkerRuntime {
  readonly navigator: Navigator;
  readonly location: Location;
}

export function shouldRegisterServiceWorker(
  runtime: ServiceWorkerRuntime,
  isProduction: boolean,
  devEnabled: boolean,
): boolean {
  if (!("serviceWorker" in runtime.navigator)) return false;
  if (
    runtime.location.protocol !== "https:" &&
    runtime.location.hostname !== "localhost"
  ) {
    return false;
  }
  return isProduction || devEnabled;
}

/**
 * Gated SW registration. PWA master AND `pwa.service_worker_enabled` must
 * both be on (AND via `isPwaFeatureOn`) — a "dormant" build (all flags
 * off) never registers any SW.
 *
 * Legacy `VITE_ENABLE_PWA_DEV` env stays as a second line of defense in
 * dev so a flag flip in DB doesn't auto-register local dev SW.
 *
 * Field diagnostic (operator Samsung S8 / Chrome 90): the early-return
 * branches each emit a `[pwa]` debug log when `VITE_DEBUG_PWA=1` or
 * `?pwa-debug=1` is on, so remote DevTools can pinpoint WHY install was
 * not offered.
 */
export function registerQuartelServiceWorker(
  flags: PwaFlagSnapshot,
  runtime: ServiceWorkerRuntime = window,
): void {
  if (!isPwaFeatureOn(flags, "pwa.service_worker_enabled")) {
    pwaDebugWarn("sw.skip: pwa.service_worker_enabled gate off", {
      master: flags["pwa.enabled"],
      sw: flags["pwa.service_worker_enabled"],
    });
    return;
  }
  const devEnabled = import.meta.env.VITE_ENABLE_PWA_DEV === "1";
  if (!shouldRegisterServiceWorker(runtime, import.meta.env.PROD, devEnabled)) {
    pwaDebugWarn("sw.skip: shouldRegisterServiceWorker returned false", {
      protocol: runtime.location.protocol,
      hostname: runtime.location.hostname,
      isProduction: import.meta.env.PROD,
      devEnabled,
      hasServiceWorker: "serviceWorker" in runtime.navigator,
    });
    return;
  }
  pwaDebugLog("sw.register: dispatching /sw.js with scope /");
  runtime.navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .then((registration) => {
      pwaDebugLog("sw.register.ok", {
        scope: registration.scope,
        state: registration.active?.state ?? "installing",
      });
    })
    .catch((error: unknown) => {
      pwaDebugWarn("sw.register.fail", { error: stringifyError(error) });
    });
}

function stringifyError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}
